import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import * as Highcharts from 'highcharts';
import { Editor, Toolbar, toHTML } from 'ngx-editor';
import { HospitalDashboardGoal } from 'src/app/leader-toolkit/models/hospital-dashboard.model';
import { buildTrajectoryChartOptions } from '../../hospital-dashboard-chart.util';

@Component({
  selector: 'vc-hospital-goal-card',
  templateUrl: './hospital-goal-card.component.html',
  styleUrl: './hospital-goal-card.component.scss',
  standalone: false,
})
export class HospitalGoalCardComponent implements OnChanges, OnInit, OnDestroy {
  @Input() goal: HospitalDashboardGoal | null = null;
  @Input() isOpen = false;
  @Input() autoFocus = false;
  @Input() isHM = false;
  @Input() currentUserName = '';
  @Input() readOnly = false;

  @Output() toggle = new EventEmitter<void>();
  @Output() addTask = new EventEmitter<HospitalDashboardGoal>();
  @Output() openTask = new EventEmitter<number>();
  @Output() taskToggled = new EventEmitter<{ goal: HospitalDashboardGoal; task: any }>();
  @Output() postNote = new EventEmitter<{ goal: HospitalDashboardGoal; text: string; isHMVisible: boolean }>();
  @Output() updateNote = new EventEmitter<{ goal: HospitalDashboardGoal; note: any; text: string; isHMVisible: boolean }>();
  @Output() deleteNote = new EventEmitter<{ goal: HospitalDashboardGoal; note: any }>();

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};

  noteText = '';
  noteIsHmVisible = true;

  editor: Editor = new Editor();
  editEditor: Editor = new Editor();
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  editingNote: any = null;
  editNoteText = '';
  editNoteIsHmVisible = true;

  confirmDeleteNote: any = null;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.editor = new Editor();
    this.editEditor = new Editor();
  }

  ngOnDestroy(): void {
    this.editor.destroy();
    this.editEditor.destroy();
  }

  sanitizeHtml(text: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(text);
  }

  get statusClass(): string {
    const s = (this.goal?.status || '').toString().toUpperCase().replace(/\s+/g, '_');
    return s === 'ON_TRACK' ? 'on' : s === 'OFF_TRACK' ? 'off' : 'risk';
  }

  get statusLabel(): string {
    const s = (this.goal?.status || '').toString().toUpperCase().replace(/\s+/g, '_');
    return s === 'ON_TRACK' ? 'On Track' : s === 'OFF_TRACK' ? 'Off Track' : 'At Risk';
  }

  get barColor(): string {
    const s = (this.goal?.status || '').toString().toUpperCase().replace(/\s+/g, '_');
    return s === 'ON_TRACK' ? '#0f9d89' : s === 'OFF_TRACK' ? '#dc2626' : '#f59e0b';
  }

  get progressBarWidth(): string {
    const p = this.goal?.progressPercent ?? 0;
    return Math.min(100, Math.max(0, p)) + '%';
  }

  get elapsedTick(): string {
    return Math.min(100, Math.max(0, this.goal?.quarterElapsedPercent ?? 0)) + '%';
  }

  get momentumIcon(): string {
    const m = (this.goal?.momentum || '').toString().toUpperCase();
    if (m === 'IMPROVING') return 'arrow_upward';
    if (m === 'DECLINING') return 'arrow_downward';
    if (m === 'FLAT') return 'remove';
    return 'remove';
  }

  get momentumLabel(): string {
    const m = (this.goal?.momentum || '').toString().toUpperCase();
    if (m === 'IMPROVING') return 'Improving';
    if (m === 'DECLINING') return 'Declining';
    if (m === 'FLAT') return 'Flat 3 weeks';
    return 'No change';
  }

  get momentumClass(): string {
    const m = (this.goal?.momentum || '').toString().toUpperCase();
    if (m === 'IMPROVING') return 'up';
    if (m === 'DECLINING') return 'down';
    return 'flat';
  }

  get completedTaskCount(): number {
    return (this.goal?.tasks || []).filter((t) => t.status === 'complete').length;
  }

  get totalTaskCount(): number {
    return (this.goal?.tasks || []).length;
  }

  get goalNumberKey(): number {
    const num = Number(this.goal?.goalNumber);
    return isNaN(num) ? -1 : num;
  }

  get isOnTrack(): boolean {
    return this.goal?.gap === null || this.goal?.gap === undefined;
  }

  get toReachGoalText(): string {
    if (this.isOnTrack) {
      return 'On Track';
    }
    const value = this.goal?.runRatePerWeek;
    if (value === null || value === undefined) {
      return '—';
    }
    const formatted = this.formatValue(value, this.getGoalFormatType());
    const uom = this.goal?.uom ? this.goal.uom : this.getGapToTargetUom();
    if (value === 0 || (uom || '').toString().trim().toUpperCase() === 'ON TRACK') {
      return 'On Track';
    }
    return uom ? `${formatted} ${uom}` : formatted;
  }

  get gapToTargetText(): string {
    return this.formatValue(this.goal?.gap, this.getGoalFormatType());
  }

  getGapToTargetUom(): string {
    switch (this.goalNumberKey) {
      case 1:
        return 'of incremental revenue per week';
      case 2:
        return 'incremental service visits per DVM day';
      case 3:
        return 'clients to use Home Delivery';
      case 4:
        return 'Incremental Revenue per Week';
      case 5:
        return '';
      case 6:
        return 'Material Decrease per Week';
      case 7:
        return 'Decrease in support staff hours per week';
      case 8:
        return 'Incremental number of Forward Book Appointments per week';
      case 9:
        return 'Incremental checkout links sent';
      case 10:
        return 'Incremental new patients per week';
      case 11:
        return 'Incremental preventative patients per week';
      case 12:
        return 'Total incremental client contacts needed';
      case 13:
        return 'Incremental insurance transactions per week';
      case 14:
        return 'Incremental wellness labs per week';
      case 15:
        return 'Additional client show rate as a percentage of remaining appointments in the quarter.';
      case 16:
        return 'Total additional client call answers required';
      default:
        return '';
    }
  }

  formatValue(value: number | null | undefined, type: any): string {
    if (value === null || value === undefined) {
      return '—';
    }
    const num = Number(value);
    if (isNaN(num)) {
      return String(value);
    }
    const typeStr = (type || '').toString().trim();
    switch (typeStr) {
      case '$': {
        if (num < 0) {
          return '-$' + Math.abs(num).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
        return '$' + num.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
      case '%':
        return num + '%';
      case '#':
        return num.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
      default:
        return num.toString();
    }
  }

  getGoalFormatType(): string {
    switch (this.goalNumberKey) {
      case 1:
      case 4:
      case 6:
        return '$';
      case 15:
        return '%';
      case 2:
      case 3:
      case 7:
      case 8:
      case 9:
      case 10:
      case 12:
      case 13:
      case 14:
      case 16:
        return '#';
      default:
        return '';
    }
  }

  isTaskDone(task: any): boolean {
    return task?.status === 'complete';
  }

  isTaskPastDue(task: any): boolean {
    return task?.status === 'past-due';
  }

  onToggleClick(): void {
    this.toggle.emit();
  }

  onTaskToggle(task: any, event: Event): void {
    if (this.readOnly) {
      event.preventDefault();
      (event.target as HTMLInputElement).checked = false;
      return;
    }
    const checked = (event.target as HTMLInputElement)?.checked;
    if (checked && this.goal) {
      this.taskToggled.emit({ goal: this.goal, task });
    }
  }

  onPostNote(): void {
    if (this.readOnly) {
      return;
    }
    let htmlContent: string;
    try {
      if (this.editor?.view?.state?.doc) {
        const textContent = this.editor.view.state.doc.textContent?.trim();
        if (!textContent) return;
        htmlContent = toHTML(this.editor.view.state.doc.toJSON() as Record<string, unknown>, this.editor.schema);
      } else {
        const text = this.noteText?.trim();
        if (!text || !this.goal) return;
        htmlContent = typeof this.noteText === 'string' ? this.noteText : text;
      }
    } catch {
      const text = this.noteText?.trim();
      if (!text || !this.goal) return;
      htmlContent = typeof this.noteText === 'string' ? this.noteText : text;
    }
    if (!htmlContent?.trim() || !this.goal) return;
    this.postNote.emit({ goal: this.goal, text: htmlContent, isHMVisible: this.noteIsHmVisible });
    this.noteText = '';
    this.noteIsHmVisible = true;
    try {
      if (this.editor?.view?.state?.doc) {
        // editor state updated internally
      }
    } catch {}
  }

  isOwnNote(note: any): boolean {
    const currentUser = (this.currentUserName || '').toString().trim().toLowerCase();
    if (!currentUser) {
      return false;
    }
    const noteAuthor = (note?.author || '').toString().trim().toLowerCase();
    if (noteAuthor && noteAuthor === currentUser) {
      return true;
    }
    const noteDisplay = (note?.displayName || note?.createdByName || note?.userName || '').toString().trim().toLowerCase();
    if (noteDisplay && noteDisplay === currentUser) {
      return true;
    }
    const noteEmail = (note?.email || note?.createdByEmail || '').toString().trim().toLowerCase();
    if (noteEmail && noteEmail === currentUser) {
      return true;
    }
    return false;
  }

  startEditNote(note: any): void {
    if (this.readOnly) {
      return;
    }
    this.editingNote = note;
    this.editNoteText = note?.text || '';
    this.editNoteIsHmVisible = !!note?.isHMVisible;
    try {
      if (!this.editEditor) {
        this.editEditor = new Editor();
      }
    } catch {}
  }

  cancelEditNote(): void {
    this.editingNote = null;
    this.editNoteText = '';
    this.editNoteIsHmVisible = true;
  }

  saveEditNote(): void {
    if (this.readOnly) {
      return;
    }
    let htmlContent: string;
    try {
      if (this.editEditor?.view?.state?.doc) {
        const textContent = this.editEditor.view.state.doc.textContent?.trim();
        if (!textContent) return;
        htmlContent = toHTML(this.editEditor.view.state.doc.toJSON() as Record<string, unknown>, this.editEditor.schema);
      } else {
        const text = this.editNoteText?.trim();
        if (!text || !this.goal || !this.editingNote) return;
        htmlContent = typeof this.editNoteText === 'string' ? this.editNoteText : text;
      }
    } catch {
      const text = this.editNoteText?.trim();
      if (!text || !this.goal || !this.editingNote) return;
      htmlContent = typeof this.editNoteText === 'string' ? this.editNoteText : text;
    }
    if (!htmlContent?.trim() || !this.goal || !this.editingNote) return;
    this.updateNote.emit({ goal: this.goal, note: this.editingNote, text: htmlContent, isHMVisible: this.editNoteIsHmVisible });
    this.editingNote = null;
    this.editNoteText = '';
    this.editNoteIsHmVisible = true;
  }

  confirmDeleteNoteDialog(note: any): void {
    if (this.readOnly) {
      return;
    }
    this.confirmDeleteNote = note;
  }

  cancelDeleteNote(): void {
    this.confirmDeleteNote = null;
  }

  deleteNoteConfirm(): void {
    if (this.readOnly || !this.goal || !this.confirmDeleteNote) return;
    this.deleteNote.emit({ goal: this.goal, note: this.confirmDeleteNote });
    this.confirmDeleteNote = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['goal'] && this.goal?.trajectory) {
      this.chartOptions = buildTrajectoryChartOptions(this.goal.trajectory);
    }
  }
}